import { useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { SelectButton } from 'primereact/selectbutton';
import { angleOf } from './engine/polar';

/* Ručno nameštanje listića kad snimak ne valja (više listića na slici, iskrivljen sken, pogrešno prepoznat krug ili rotacija).
   Prikaz je kao glavni (DiscView): listić je OKRENUT u UTC (rotacija + pomeraj Beograda) oko svog centra, spolja je UTC skala,
   a vodič (krug ivice papira, krug trake) stoji u sredini. Operater pomera i okreće SLIKU dok se ivica papira ne poklopi sa krugom,
   a odštampani sati sa UTC skalom (leti 12 sa listića = 10 UTC): vuče unutar kruga = pomera listić, vuče van kruga = okreće ga,
   točkić = veličina kruga (Shift = brže); dugmad za fino podešavanje. "Primeni" ponovo čita traku sa zadatim položajem
   (engine analyzeImage(img, polozaj)), pa se aktivnosti prikazuju i unose u UTC kao i do sada. */

const TIPOVI = [{ label: 'Kienzle', value: 'kienzle' }, { label: 'BLU-8-125', value: 'blu' }, { label: 'BLU-2-125-EC', value: 'blu2' }, { label: 'FBM 2-125-24', value: 'fbm' }];
const SATI = Array.from({ length: 24 }, (_, h) => h);
const DEG = 180 / Math.PI;

export default function NamestiListic({ src, width, height, pocetni, pomak = 0, utc = false, krugovi = [], onPrimeni, onOtkazi }) {
    const [p, setP] = useState(pocetni);
    const svgRef = useRef(null);
    const vuce = useRef(null);
    const pRef = useRef(p);
    pRef.current = p;
    const { cx, cy, r, rotMin } = p;
    const izmeni = (patch) => setP((x) => ({ ...x, ...patch }));
    /* ugao slike u radijanima (kao DiscView: -(rotacija + pomak) minuta) */
    const ugaoOf = (x) => (-(x.rotMin + pomak) * 2 * Math.PI) / 1440;
    const ugao = ugaoOf(p);
    const M = 1.18 * pocetni.r; // okvir prikaza je stalan (centar listića je u sredini)

    const tacka = (e) => {
        const svg = svgRef.current;
        const q = svg.createSVGPoint();
        q.x = e.clientX; q.y = e.clientY;
        return q.matrixTransform(svg.getScreenCTM().inverse());
    };
    const down = (e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        const t = tacka(e), x = pRef.current;
        vuce.current = Math.hypot(t.x, t.y) / x.r < 0.8
            ? { nacin: 'pomeri', t, cx: x.cx, cy: x.cy, ugao: ugaoOf(x) }
            : { nacin: 'okreni', a: Math.atan2(t.y, t.x), rotMin: x.rotMin };
        e.currentTarget.setPointerCapture(e.pointerId);
    };
    const move = (e) => {
        const v = vuce.current;
        if (!v) return;
        const t = tacka(e);
        if (v.nacin === 'pomeri') {
            /* slika prati miš: pomeraj na ekranu vraćen u koordinate slike (inverzna rotacija) pomera centar suprotno */
            const dx = t.x - v.t.x, dy = t.y - v.t.y, c = Math.cos(-v.ugao), s = Math.sin(-v.ugao);
            izmeni({ cx: v.cx - (dx * c - dy * s), cy: v.cy - (dx * s + dy * c) });
        } else {
            let da = Math.atan2(t.y, t.x) - v.a;
            if (da > Math.PI) da -= 2 * Math.PI;
            if (da < -Math.PI) da += 2 * Math.PI;
            izmeni({ rotMin: v.rotMin - (da * 1440) / (2 * Math.PI) });
        }
    };
    const up = () => { vuce.current = null; };
    /* točkić menja veličinu kruga (pasivni slušalac ne sme da spreči skrol stranice, pa se dodaje ručno) */
    useEffect(() => {
        const svg = svgRef.current;
        if (!svg) return undefined;
        const onWheel = (e) => {
            e.preventDefault();
            const k = e.shiftKey ? 5 : 1;
            setP((x) => ({ ...x, r: Math.max(50, x.r + (e.deltaY < 0 ? k : -k)) }));
        };
        svg.addEventListener('wheel', onWheel, { passive: false });
        return () => svg.removeEventListener('wheel', onWheel);
    }, []);

    const pt = (rr, minut) => { const a = angleOf(minut, 0); return [rr * Math.cos(a), rr * Math.sin(a)]; };
    const sw = (k) => ({ strokeWidth: k * pocetni.r });
    const R = pocetni.r;
    return (
        <div className="namesti">
            <svg ref={svgRef} className="namesti-svg" viewBox={`${-M} ${-M} ${2 * M} ${2 * M}`} role="img"
                aria-label="Nameštanje listića: vucite unutar kruga da pomerite listić, van kruga da ga okrenete, točkić menja veličinu kruga"
                onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}>
                <g transform={`rotate(${ugao * DEG}) translate(${-cx} ${-cy})`}>
                    <image href={src} x="0" y="0" width={width} height={height} preserveAspectRatio="none" />
                    {/* pronađeni krugovi listića: klik iseče taj listić i automatski ga pročita (obrazac, rotacija, centar) */}
                    {krugovi.length > 1 && krugovi.map((kr, i) => (
                        <g key={i} className="namesti-krug" role="button" tabIndex={0} aria-label={`Izaberi listić ${i + 1}`}
                            onPointerDown={(e) => e.stopPropagation()} onClick={() => onPrimeni({ krug: { cx: kr.cx, cy: kr.cy, r: kr.r } })}
                            onKeyDown={(e) => { if (e.key === 'Enter') onPrimeni({ krug: { cx: kr.cx, cy: kr.cy, r: kr.r } }); }}>
                            {/* krug se crta malo VAN ivice papira (5%) da se jasno vidi i ne prekriva štampu listića */}
                            <circle cx={kr.cx} cy={kr.cy} r={kr.r * 1.05} className="namesti-krug-hit" style={sw(0.04)} />
                            <circle cx={kr.cx} cy={kr.cy} r={kr.r * 1.05} className="namesti-krug-linija" style={sw(0.008)} />
                            <title>{`Listić ${i + 1} — klik: iseci i pročitaj automatski`}</title>
                        </g>
                    ))}
                </g>
                <circle cx={0} cy={0} r={r} className="namesti-ivica" style={sw(0.006)} />
                <circle cx={0} cy={0} r={0.5 * r} className="namesti-traka" style={sw(0.004)} />
                <line x1={-0.04 * R} y1={0} x2={0.04 * R} y2={0} className="namesti-centar" style={sw(0.005)} />
                <line x1={0} y1={-0.04 * R} x2={0} y2={0.04 * R} className="namesti-centar" style={sw(0.005)} />
                <g className="namesti-sat-skala">
                    {SATI.map((h) => {
                        const [x1, y1] = pt(1.02 * R, h * 60), [x2, y2] = pt(1.07 * R, h * 60), [tx, ty] = pt(1.12 * R, h * 60);
                        return (
                            <g key={h} className={h % 6 ? 'namesti-sat' : 'namesti-sat glavni'}>
                                <line x1={x1} y1={y1} x2={x2} y2={y2} style={sw(h % 6 ? 0.005 : 0.01)} />
                                <text x={tx} y={ty} textAnchor="middle" dominantBaseline="central" style={{ fontSize: 0.05 * R }}>{h}</text>
                            </g>
                        );
                    })}
                    <text x={-0.98 * M} y={-0.92 * M} className="namesti-oznaka" style={{ fontSize: 0.05 * R }}>{utc ? 'UTC' : 'vreme sa listića'}</text>
                </g>
            </svg>
            <div className="namesti-alati" role="toolbar" aria-label="Fino podešavanje položaja listića">
                <div className="namesti-grupa">
                    <span className="namesti-naslov">Obrazac</span>
                    <SelectButton className="namesti-obrazac" value={p.tip} options={TIPOVI} onChange={(e) => e.value && izmeni({ tip: e.value })} aria-label="Obrazac listića" />
                </div>
                <div className="namesti-grupa">
                    <span className="namesti-naslov">Okreni (min)</span>
                    <div className="namesti-dugmad">
                        <Button type="button" icon="pi pi-angle-double-left" text size="small" aria-label="Okreni listić 10 minuta unazad" tooltip="−10 min" onClick={() => izmeni({ rotMin: rotMin + 10 })} />
                        <Button type="button" icon="pi pi-angle-left" text size="small" aria-label="Okreni listić 1 minut unazad" tooltip="−1 min" onClick={() => izmeni({ rotMin: rotMin + 1 })} />
                        <Button type="button" icon="pi pi-angle-right" text size="small" aria-label="Okreni listić 1 minut unapred" tooltip="+1 min" onClick={() => izmeni({ rotMin: rotMin - 1 })} />
                        <Button type="button" icon="pi pi-angle-double-right" text size="small" aria-label="Okreni listić 10 minuta unapred" tooltip="+10 min" onClick={() => izmeni({ rotMin: rotMin - 10 })} />
                    </div>
                </div>
                <div className="namesti-grupa">
                    <span className="namesti-naslov">Krug</span>
                    <div className="namesti-dugmad">
                        <Button type="button" icon="pi pi-minus" text size="small" aria-label="Smanji krug" tooltip="Smanji (točkić)" onClick={() => izmeni({ r: Math.max(50, r - 1) })} />
                        <Button type="button" icon="pi pi-plus" text size="small" aria-label="Povećaj krug" tooltip="Povećaj (točkić)" onClick={() => izmeni({ r: r + 1 })} />
                    </div>
                </div>
                <div className="namesti-akcije">
                    <Button type="button" icon="pi pi-replay" label="Početni" text severity="secondary" size="small" aria-label="Vrati početni položaj" onClick={() => setP(pocetni)} />
                    <Button type="button" label="Otkaži" severity="secondary" outlined size="small" onClick={onOtkazi} />
                    <Button type="button" label="Primeni" icon="pi pi-check" size="small" onClick={() => onPrimeni({ ...p, rotMin: ((p.rotMin % 1440) + 1440) % 1440 })} />
                </div>
            </div>
            {krugovi.length > 1 && (
                <div className="callout info"><i className="pi pi-bullseye" /> Na snimku je {krugovi.length} listića: <b>kliknite na isprekidani krug</b> željenog listića
                    — iseče se i pročita automatski; po potrebi ga posle dotegnite ovde.</div>
            )}
            <p className="bulk-disc-help">
                Pomerite i okrenite listić tako da se ivica papira poklopi sa zelenim krugom, a sati odštampani na listiću sa skalom
                {utc ? ' u UTC (leti je 12 sa listića na 10 UTC, zimi na 11)' : ' (unesite datum aktivnosti za prikaz u UTC)'}.
                Vucite <b>unutar kruga</b> da pomerite, <b>van kruga</b> da okrenete; točkić miša menja veličinu kruga (Shift = brže).
            </p>
        </div>
    );
}
